import { UAContext } from '@quentin-sommer/react-useragent';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import Button from '~components/button';
import StatusButton from '~components/navigation-panel/status-button';
import Typography from '~components/typography';
import { SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings_targetBookings } from '~graphql/__generated__/SP_QueryStudentGetBookings';
import { Router, Trans, useTranslation } from '~i18n';
import { IconRight, More, Time } from '~svg-components';
import { amendmentCountDown } from '~utils/count-down-time';
import { notEmpty } from '~utils/js-helper';

interface IExtensionCardProps {
  booking: any;
}

const ExtensionCard: React.FC<IExtensionCardProps> = ({ booking }) => {
  const uaResults = useContext(UAContext).uaResults as UAResults;
  const isMobile = uaResults.mobile;
  const { t } = useTranslation(['student_portal']);
  const [startTime, setStartTime] = useState<any>(moment());
  const [countDownResult, setCountDownResult] = useState('00:00');

  useEffect(() => {
    const leftTime = amendmentCountDown(
      startTime,
      moment(booking?.heldToTime || booking?.countDown)
    );
    const timer = setInterval(() => {
      setStartTime(moment());
    }, 30000);
    if (leftTime === 0) {
      clearInterval(timer);
      setCountDownResult('00:00');
      return;
    }
    setCountDownResult(leftTime);
    return () => {
      clearInterval(timer);
    };
  }, [booking, startTime]);

  const cardName = [
    booking?.roomAllocation?.bedName,
    booking?.roomAllocation?.buildingName,
    `${booking?.property?.name} in ${booking?.property?.city?.name}`,
  ].filter(notEmpty);
  return (
    <div className="extension-card">
      <div className="extension-card__wrapper">
        <div className="extension-card__info">
          <div className="extension-card__title">
            <Typography variant={isMobile ? 'h8' : 'h7'}>{cardName.join(', ')}</Typography>
            {isMobile && (
              <div className="extension-card__label-mobile">
                <Typography variant="body3" color="var(--color-white)">
                  {t('amendment.extension.pending')}
                </Typography>
              </div>
            )}
            {!isMobile && (
              <StatusButton
                status="warning"
                name={t('amendment.extension.pending')}
                className="extension-card__label"
              />
            )}
          </div>
          <div className="extension-card__tenancy">
            <Typography variant="body3">
              {moment(booking?.tenancyOption?.startDate).format('ddd, ll')}
            </Typography>
            <IconRight className="extension-card__tenancy-icon" fill="var(--color-grey4c)" />
            <Typography variant="body3">
              {moment(booking?.tenancyOption?.endDate).format('ddd, ll')}
            </Typography>
          </div>
          <div className="extension-card__timer">
            {!isMobile && <Time width="16" height="16" fill="var(--color-red_6)" />}
            {booking?.heldToTime && (
              <div className="extension-card__count-down">
                <Typography variant="body3">{t('amendment.extension.count_down_title')}</Typography>
                <Typography variant="body3">
                  <Trans i18nKey="amendment.extension.held_bed_subTitle">
                    You have till{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      color="var(--color-red_6)"
                      fontWeight={600}
                    >
                      {moment(booking?.heldToTime).format('lll')}
                    </Typography>{' '}
                    to respond to the amendment.
                  </Trans>
                </Typography>
              </div>
            )}
            {booking?.countDown && !booking?.heldToTime && (
              <div className="extension-card__count-down">
                <Typography variant="body3">{t('amendment.extension.count_down_title')}</Typography>
                <Typography variant="body3" color="var(--color-red_6)" fontWeight={600}>
                  {countDownResult}
                </Typography>
                <Typography variant="body3">
                  {t('amendment.extension.count_down_subTitle')}
                </Typography>
              </div>
            )}
          </div>
        </div>
        {!isMobile && (
          <Button
            color="secondary"
            onClick={() => {
              Router.push(`/amendment-process?bookingId=${booking?.id}`);
            }}
          >
            {t('amendment.go_confirm')}
          </Button>
        )}
        {isMobile && (
          <div
            className="extension-card__href"
            onClick={() => {
              Router.push(`/amendment-process?bookingId=${booking?.id}`);
            }}
          >
            <Typography
              variant="body3"
              cursor="pointer"
              textDecoration="underline"
              onClick={() => {
                Router.push(`/booking-details?bookingId=${booking?.id}`);
              }}
            >
              {t('amendment.go_confirm')}
            </Typography>
            <div className="extension-card__more">
              <More
                className="extension-card__more-icon"
                fill="var(--color-purple_6)"
                width="7"
                height="8"
              />
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .extension-card {
          padding: 24px;
          &__wrapper {
            padding: 20px 24px;
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: var(--color-purple_1);
          }
          &__timer {
            display: flex;
            align-items: center;
            margin-top: 12px;
          }
          &__count-down {
            margin-left: 8px;
          }
          &__title {
            display: flex;
            align-items: center;
          }
          &__tenancy {
            display: flex;
            align-items: center;
            margin-top: 4px;
          }
          :global(&__tenancy-icon) {
            width: 12px;
            margin-left: 8px;
            margin-right: 8px;
          }
          :global(&__label) {
            margin-left: 8px;
          }
        }
        @media screen and (max-width: 768px) {
          .extension-card {
            padding: 0 0 20px;
            &__href {
              display: flex;
              align-items: center;
              margin-top: 8px;
            }
            &__more {
              width: 20px;
              height: 20px;
              border-radius: 10px;
              margin-left: 8px;
              background: var(--color-yellow_6);
              display: flex;
              align-items: center;
              justify-content: center;
            }
            :global(&__more-icon) {
              width: 8px;
              height: 8px;
              transform: rotate(-90deg);
            }
            &__wrapper {
              padding: 34px 12px 16px;
              display: block;
              position: relative;
            }
            &__label-mobile {
              position: absolute;
              top: 0;
              left: 0;
              background: var(--color-yellow_6);
              padding: 0 12px;
              border-radius: 14px 0 14px 0;
            }
            &__timer {
              display: block;
            }
            &__count-down {
              margin-left: 0;
            }
            &__title {
              display: block;
            }
          }
        }
      `}</style>
    </div>
  );
};

export default ExtensionCard;
